import { BASE_BACKEND_URL, fetchWithAuth } from '../../../auth'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import LoadingScreen from '../../../components/LoadingScreen'
import {
  isSameStoreRelationSalesman,
  requiresStoreRelationPrincipalReassignment
} from '../helpers/storeRelationAssignmentUtils'
import { STORE_RELATIONS_SAVE_SUCCESS_MESSAGES } from '../constants/storeConstants'
import { fetchStoreRelationBrandSearch } from '../hooks/useStoreRelationBrandSearch'
import useFetchStoreShow from '../hooks/useFetchStoreShow'
import useStoreRelationBaseInfoOptions from '../hooks/useStoreRelationBaseInfoOptions'
import useSaveStoreRelation from '../hooks/useSaveStoreRelation'
import useStoreRelationSalesmanSearch from '../hooks/useStoreRelationSalesmanSearch'
import ReassignUserModal from '../components/modal/ReassignUserModal'
import StoreRelationBaseInfoSection from './components/StoreRelationBaseInfoSection'
import StoreRelationBrandAssignmentsSection from './components/StoreRelationBrandAssignmentsSection'
import StoreRelationFormAlerts from './components/StoreRelationFormAlerts'
import StoreRelationFormHeader from './components/StoreRelationFormHeader'
import StoreRelationPrincipalSection from './components/StoreRelationPrincipalSection'
import {
  STORE_RELATION_FORM_ALERT_DURATIONS,
  STORE_RELATION_FORM_MODES,
  STORE_RELATION_FORM_TEXT
} from './constants/storeRelationFormConstants'
import {
  buildStoreRelationCurrentReassignUser,
  buildStoreRelationEffectivePrincipalResolutionContext,
  buildStoreRelationExistingRelationFlowState,
  buildStoreRelationFormReassignmentWarningMessages,
  buildStoreRelationPageDescription,
  buildStoreRelationPageTitle,
  buildStoreRelationReassignUserOptions,
  buildStoreRelationRowsRequiringReassignmentLabel,
  buildStoreRelationSaveButtonLabel,
  createStoreRelationFlowState,
  createStoreRelationReassignModalState
} from './helpers/storeRelationFormFlowHelpers'
import {
  buildStoreRelationBrandValidationRows,
  buildStoreRelationEditBaseInfoOptions,
  buildStoreRelationInitialAssignedBrandDbRefs,
  buildStoreRelationInitialBrandOptionsByDbRef,
  buildStoreRelationInitialFormState,
  buildStoreRelationNewAssignmentRow,
  buildStoreRelationResetClientDependentState,
  buildStoreRelationSelectedBrandDbRefsByRow,
  hasStoreRelationPayload
} from './helpers/storeRelationFormStateHelpers'
import {
  mergeStoreRelationBrandOptions,
  resolveStoreRelationAutocompleteSearchTerm
} from './utils/storeRelationFormOptionUtils'

const StoreRelationFormView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = Boolean(id)
  const statePayload = location.state?.storeRelationData || null
  const flowAction = location.state?.flowAction || ''
  const lockBaseInfo = Boolean(location.state?.lockBaseInfo)
  const initialInfoMessage = location.state?.flowMessage || ''
  const principalResolutionContext = location.state?.principalResolutionContext || null
  const pageTitleOverride = location.state?.pageTitle || ''
  const initialBrandValidationAlerts = location.state?.brandValidationAlerts
  const returnTo =
    location.state?.returnTo || (isEditMode ? `/store_relations/${id}` : '/store_relations')
  const hasPrefilledLockedPayload =
    !isEditMode && lockBaseInfo && hasStoreRelationPayload(statePayload)
  const shouldFetchDetails = isEditMode
  const { data, loading } = useFetchStoreShow(shouldFetchDetails ? id : null)

  const storeRelationData = useMemo(() => {
    if (!isEditMode) return null

    const fetchedStoreRelation = data?.result || data || null

    if (hasStoreRelationPayload(fetchedStoreRelation)) {
      return fetchedStoreRelation
    }

    if (hasStoreRelationPayload(statePayload)) {
      return statePayload
    }

    return null
  }, [data, isEditMode, statePayload])

  const [resolvedExistingRelationData, setResolvedExistingRelationData] = useState(null)
  const effectiveStoreRelationData = isEditMode
    ? storeRelationData
    : resolvedExistingRelationData || (hasPrefilledLockedPayload ? statePayload : null)
  const initialFormState = useMemo(
    () => buildStoreRelationInitialFormState(effectiveStoreRelationData),
    [effectiveStoreRelationData]
  )
  const [formState, setFormState] = useState(initialFormState)
  const [pageInfoMessage, setPageInfoMessage] = useState(initialInfoMessage)
  const [brandValidationAlerts, setBrandValidationAlerts] = useState(
    () => initialBrandValidationAlerts || []
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [unavailableBrandDbRefs, setUnavailableBrandDbRefs] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [flowState, setFlowState] = useState(() =>
    createStoreRelationFlowState(
      isEditMode ? STORE_RELATION_FORM_MODES.EDIT : STORE_RELATION_FORM_MODES.CREATE,
      id || null
    )
  )
  const [saveErrorMessage, setSaveErrorMessage] = useState('')
  const saveInFlightRef = useRef(false)
  const [reassignModalState, setReassignModalState] = useState(
    createStoreRelationReassignModalState
  )
  const isPrefilledAssignMode = hasPrefilledLockedPayload && flowAction === 'assign_principal'
  const isPrefilledResolutionMode = hasPrefilledLockedPayload

  const supplierSearchTerm = useMemo(
    () =>
      resolveStoreRelationAutocompleteSearchTerm({
        option: formState.supplierOption,
        searchTerm: formState.supplierSearch,
        shouldDisableSearch: isEditMode || isPrefilledResolutionMode
      }),
    [formState.supplierOption, formState.supplierSearch, isEditMode, isPrefilledResolutionMode]
  )

  const buyerSearchTerm = useMemo(
    () =>
      resolveStoreRelationAutocompleteSearchTerm({
        option: formState.buyerOption,
        searchTerm: formState.buyerSearch,
        shouldDisableSearch: isEditMode || isPrefilledResolutionMode
      }),
    [formState.buyerOption, formState.buyerSearch, isEditMode, isPrefilledResolutionMode]
  )

  const referenceSearchTerm = useMemo(
    () =>
      resolveStoreRelationAutocompleteSearchTerm({
        option: formState.referenceOption,
        searchTerm: formState.referenceSearch,
        shouldDisableSearch: isEditMode || isPrefilledResolutionMode
      }),
    [formState.referenceOption, formState.referenceSearch, isEditMode, isPrefilledResolutionMode]
  )

  const clientSearchTerm = useMemo(
    () =>
      resolveStoreRelationAutocompleteSearchTerm({
        option: formState.clientOption,
        searchTerm: formState.clientSearch,
        shouldDisableSearch: isEditMode || isPrefilledResolutionMode
      }),
    [formState.clientOption, formState.clientSearch, isEditMode, isPrefilledResolutionMode]
  )

  const principalUserSearchTerm = useMemo(
    () =>
      resolveStoreRelationAutocompleteSearchTerm({
        option: formState.principalUserOption,
        searchTerm: formState.principalUserSearch
      }),
    [formState.principalUserOption, formState.principalUserSearch]
  )

  const supplierOptionsState = useStoreRelationBaseInfoOptions({
    type: 'suppliers',
    enabled:
      !isEditMode &&
      !isPrefilledResolutionMode &&
      flowState.mode !== STORE_RELATION_FORM_MODES.EDIT,
    searchTerm: supplierSearchTerm
  })

  const buyerOptionsState = useStoreRelationBaseInfoOptions({
    type: 'buyers',
    enabled:
      !isEditMode &&
      !isPrefilledResolutionMode &&
      flowState.mode !== STORE_RELATION_FORM_MODES.EDIT &&
      Boolean(formState.supplierOption?.value),
    searchTerm: buyerSearchTerm,
    supplierId: formState.supplierOption?.value || null
  })

  const referenceOptionsState = useStoreRelationBaseInfoOptions({
    type: 'references',
    enabled:
      !isEditMode &&
      !isPrefilledResolutionMode &&
      flowState.mode !== STORE_RELATION_FORM_MODES.EDIT &&
      Boolean(formState.supplierOption?.value && formState.buyerOption?.value),
    searchTerm: referenceSearchTerm,
    supplierId: formState.supplierOption?.value || null,
    buyerId: formState.buyerOption?.value || null
  })

  const clientOptionsState = useStoreRelationBaseInfoOptions({
    type: 'clients',
    enabled:
      !isEditMode &&
      !isPrefilledResolutionMode &&
      flowState.mode !== STORE_RELATION_FORM_MODES.EDIT &&
      Boolean(
        formState.supplierOption?.value &&
        formState.buyerOption?.value &&
        formState.referenceOption?.value
      ),
    searchTerm: clientSearchTerm,
    supplierId: formState.supplierOption?.value || null,
    buyerId: formState.buyerOption?.value || null,
    storeReference: formState.referenceOption?.value || ''
  })

  const { options: principalSalesmanOptions, loading: principalSalesmenLoading } =
    useStoreRelationSalesmanSearch(principalUserSearchTerm)

  const { options: assignmentSalesmanOptions, loading: assignmentSalesmenLoading } =
    useStoreRelationSalesmanSearch(formState.assignmentUserSearch)

  const { options: reassignSalesmanOptions, loading: reassignSalesmenLoading } =
    useStoreRelationSalesmanSearch(reassignModalState.nextUserSearch)

  const { saveStoreRelation } = useSaveStoreRelation()
  const selectedClientFlags = formState.clientOption?.raw?.flags || null
  const isResolvedEditMode = isEditMode || flowState.mode === STORE_RELATION_FORM_MODES.EDIT
  const shouldRenderReadOnlyBaseInfo = isResolvedEditMode || isPrefilledResolutionMode
  const canSplitOrderByBrand = Boolean(
    isEditMode
      ? effectiveStoreRelationData?.flags?.split_order_by_brand
      : selectedClientFlags?.split_order_by_brand
  )
  const isBaseInfoStepCompleted = Boolean(
    formState.supplierOption?.value &&
    formState.buyerOption?.value &&
    formState.referenceOption?.value &&
    formState.clientOption?.db_ref
  )
  const isClientStepCompleted = Boolean(formState.clientOption?.db_ref)
  const isPrincipalUserStepCompleted = Boolean(formState.principalUserOption?.db_ref)
  const hasPrincipalReassignmentRequirement = requiresStoreRelationPrincipalReassignment(
    effectiveStoreRelationData
  )
  const hasResolvedPrincipalReassignment =
    !hasPrincipalReassignmentRequirement || !effectiveStoreRelationData?.client_principal?.salesman
      ? true
      : !isSameStoreRelationSalesman(
          formState.principalUserOption,
          effectiveStoreRelationData.client_principal.salesman
        )
  const hasPendingPrincipalReassignment =
    hasPrincipalReassignmentRequirement && !hasResolvedPrincipalReassignment

  const filteredPrincipalSalesmanOptions = useMemo(() => {
    if (!hasPrincipalReassignmentRequirement) return principalSalesmanOptions

    const currentPrincipalSalesman = effectiveStoreRelationData?.client_principal?.salesman

    return principalSalesmanOptions.filter(
      (option) => !isSameStoreRelationSalesman(option, currentPrincipalSalesman)
    )
  }, [
    effectiveStoreRelationData?.client_principal?.salesman,
    hasPrincipalReassignmentRequirement,
    principalSalesmanOptions
  ])

  const canSaveForm = isClientStepCompleted && isPrincipalUserStepCompleted
  const showBrandAssignmentsDisabledAlert =
    canSplitOrderByBrand &&
    !formState.shouldAssignUsersByBrand &&
    formState.assignmentRows.length > 0

  const rowsRequiringReassignment = useMemo(
    () => formState.assignmentRows.filter((row) => row.requiresReassignment),
    [formState.assignmentRows]
  )

  const hasPendingUserReassignments = rowsRequiringReassignment.length > 0

  const rowsRequiringReassignmentLabel = useMemo(
    () => buildStoreRelationRowsRequiringReassignmentLabel(rowsRequiringReassignment),
    [rowsRequiringReassignment]
  )

  const reassignmentWarningMessages = useMemo(
    () =>
      buildStoreRelationFormReassignmentWarningMessages({
        hasPendingPrincipalReassignment,
        rowsRequiringReassignmentLabel
      }),
    [hasPendingPrincipalReassignment, rowsRequiringReassignmentLabel]
  )

  const editBaseInfoOptions = useMemo(
    () =>
      buildStoreRelationEditBaseInfoOptions({
        formState,
        effectiveStoreRelationData
      }),
    [effectiveStoreRelationData, formState]
  )

  const handleSearchChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const clearClientDependentFeedback = () => {
    setBrandValidationAlerts([])
    setUnavailableBrandDbRefs([])
    setResolvedExistingRelationData(null)
    setSaveErrorMessage('')
    setFlowState(createStoreRelationFlowState())
  }

  const applyResolvedRelationData = (result) => {
    if (!hasStoreRelationPayload(result)) return

    setResolvedExistingRelationData(result)
    setFormState(buildStoreRelationInitialFormState(result))
  }

  const applyExistingRelationFlow = (response) => {
    setFlowState(buildStoreRelationExistingRelationFlowState(response))
    applyResolvedRelationData(response?.result)
  }

  const handleSupplierChange = (_, nextOption) => {
    clearClientDependentFeedback()

    setFormState((prev) => ({
      ...prev,
      supplierOption: nextOption,
      supplierSearch: nextOption?.label || '',
      buyerOption: null,
      buyerSearch: '',
      referenceOption: null,
      referenceSearch: '',
      ...buildStoreRelationResetClientDependentState()
    }))
  }

  const handleBuyerChange = (_, nextOption) => {
    clearClientDependentFeedback()

    setFormState((prev) => ({
      ...prev,
      buyerOption: nextOption,
      buyerSearch: nextOption?.label || '',
      referenceOption: null,
      referenceSearch: '',
      ...buildStoreRelationResetClientDependentState()
    }))
  }

  const handleReferenceChange = (_, nextOption) => {
    clearClientDependentFeedback()

    setFormState((prev) => ({
      ...prev,
      referenceOption: nextOption,
      referenceSearch: nextOption?.label || '',
      ...buildStoreRelationResetClientDependentState()
    }))
  }

  const handleClientChange = (_, nextOption) => {
    clearClientDependentFeedback()

    setFormState((prev) => ({
      ...prev,
      clientOption: nextOption,
      clientSearch: nextOption?.label || '',
      ...buildStoreRelationResetClientDependentState()
    }))
  }

  const handlePrincipalUserChange = (nextOption) => {
    setFormState((prev) => ({
      ...prev,
      principalUserOption: nextOption,
      principalUserSearch: nextOption?.label || ''
    }))
  }

  const handleToggleAssignUsersByBrand = (checked) => {
    setFormState((prev) => ({
      ...prev,
      shouldAssignUsersByBrand: checked
    }))
  }

  useEffect(() => {
    setFormState(initialFormState)
  }, [initialFormState])

  useEffect(() => {
    setPageInfoMessage(initialInfoMessage)
  }, [initialInfoMessage])

  useEffect(() => {
    if (isEditMode) {
      setResolvedExistingRelationData(null)
      setFlowState(createStoreRelationFlowState(STORE_RELATION_FORM_MODES.EDIT, id || null))
    }
  }, [id, isEditMode])

  useEffect(() => {
    setBrandValidationAlerts(initialBrandValidationAlerts || [])
  }, [initialBrandValidationAlerts])

  useEffect(() => {
    if (showValidationErrors && canSaveForm) {
      setShowValidationErrors(false)
    }
  }, [canSaveForm, showValidationErrors])

  useEffect(() => {
    setUnavailableBrandDbRefs([])
  }, [initialFormState])

  useEffect(() => {
    setReassignModalState(createStoreRelationReassignModalState())
  }, [initialFormState])

  useEffect(() => {
    if (!brandValidationAlerts.length) return undefined

    const timeoutId = setTimeout(() => {
      setBrandValidationAlerts([])
    }, STORE_RELATION_FORM_ALERT_DURATIONS.BRAND_VALIDATION)

    return () => clearTimeout(timeoutId)
  }, [brandValidationAlerts])

  useEffect(() => {
    if (flowState.mode !== STORE_RELATION_FORM_MODES.EDIT || !flowState.message || isEditMode) {
      return undefined
    }

    const timeoutId = setTimeout(() => {
      setFlowState((prev) => ({
        ...prev,
        message: ''
      }))
    }, STORE_RELATION_FORM_ALERT_DURATIONS.FLOW_INFO)

    return () => clearTimeout(timeoutId)
  }, [flowState.message, flowState.mode, isEditMode])

  useEffect(() => {
    if (!isResolvedEditMode) return undefined
    if (!initialFormState.clientOption?.db_ref) return undefined
    if (!initialFormState.assignmentRows.length) {
      setBrandValidationAlerts([])
      return undefined
    }

    let cancelled = false

    const validateCurrentBrands = async () => {
      const validationRows = buildStoreRelationBrandValidationRows(initialFormState.assignmentRows)

      if (!validationRows.length) {
        if (!cancelled) {
          setBrandValidationAlerts([])
        }

        return
      }

      const validationResults = await Promise.all(
        validationRows.map(async (row) => {
          try {
            const result = await fetchStoreRelationBrandSearch({
              clientDbRef: initialFormState.clientOption?.db_ref,
              salesmanDbRef: row.salesmanDbRef,
              searchTerm: '',
              currentBrandDbRefs: row.brandDbRefs
            })

            const nextUnavailableBrandDbRefs = (result.meta?.currentBrandsStatus || [])
              .filter(
                (item) =>
                  item?.available === false || ['removed', 'unavailable'].includes(item?.status)
              )
              .map((item) => item?.db_ref)
              .filter(Boolean)

            return {
              userLabel: row.userLabel,
              salesmanDbRef: row.salesmanDbRef,
              unavailableBrandDbRefs: nextUnavailableBrandDbRefs
            }
          } catch (error) {
            console.error('brand validation error on edit load', error)

            return {
              userLabel: row.userLabel,
              salesmanDbRef: row.salesmanDbRef,
              unavailableBrandDbRefs: []
            }
          }
        })
      )

      if (cancelled) return

      const nextUnavailableBrandDbRefs = [
        ...new Set(validationResults.flatMap((item) => item.unavailableBrandDbRefs || []))
      ]
      const nextAlerts =
        nextUnavailableBrandDbRefs.length > 0
          ? [
              `Las siguientes marcas no se encuentran disponibles: ${nextUnavailableBrandDbRefs.join(', ')}`
            ]
          : []

      setBrandValidationAlerts(nextAlerts)
      setUnavailableBrandDbRefs(nextUnavailableBrandDbRefs)

      if (nextAlerts.length > 0) {
        const unavailableBySalesman = new Map(
          validationResults.map((item) => [
            item.salesmanDbRef,
            new Set(item.unavailableBrandDbRefs)
          ])
        )

        setFormState((prev) => ({
          ...prev,
          assignmentRows: prev.assignmentRows.map((row) => {
            const unavailableSet = unavailableBySalesman.get(row.salesmanDbRef)

            if (!unavailableSet?.size) return row

            return {
              ...row,
              brandOptions: (row.brandOptions || []).filter(
                (brand) => !unavailableSet.has(brand?.db_ref)
              )
            }
          })
        }))
      }
    }

    validateCurrentBrands()

    return () => {
      cancelled = true
    }
  }, [initialFormState, isResolvedEditMode])

  useEffect(() => {
    if (isEditMode || !isBaseInfoStepCompleted) return undefined

    const controller = new AbortController()

    const validateExistingRelation = async () => {
      try {
        const query = new URLSearchParams({
          client_db_ref: formState.clientOption?.db_ref || '',
          supplier_id: String(formState.supplierOption?.value || ''),
          buyer_id: String(formState.buyerOption?.value || ''),
          store_reference: formState.referenceOption?.value || ''
        })

        const response = await fetchWithAuth(
          `${BASE_BACKEND_URL}/poc/store_relations_overview/existing_relation?${query.toString()}`,
          {
            signal: controller.signal
          }
        )

        if (!response?.flow) return

        if (response.flow.mode === STORE_RELATION_FORM_MODES.EDIT) {
          applyExistingRelationFlow(response)
          return
        }

        applyResolvedRelationData(response?.result)
        setFlowState(createStoreRelationFlowState())
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('validateExistingRelation error', error)
        }
      }
    }

    validateExistingRelation()

    return () => {
      controller.abort()
    }
  }, [
    formState.buyerOption?.value,
    formState.clientOption?.db_ref,
    formState.referenceOption?.value,
    formState.supplierOption?.value,
    isBaseInfoStepCompleted,
    isEditMode
  ])

  const resolvedClientDbRef =
    formState.clientOption?.db_ref || effectiveStoreRelationData?.client?.db_ref || ''

  const pageTitle = buildStoreRelationPageTitle({
    isResolvedEditMode,
    isPrefilledResolutionMode,
    pageTitleOverride,
    isPrefilledAssignMode,
    clientDbRef: resolvedClientDbRef,
    relationId: flowState.relationId,
    routeId: id
  })

  const pageDescription = buildStoreRelationPageDescription({
    isResolvedEditMode,
    isPrefilledAssignMode,
    isPrefilledResolutionMode
  })

  const saveButtonLabel = buildStoreRelationSaveButtonLabel({
    isResolvedEditMode,
    isPrefilledAssignMode,
    shouldRenderReadOnlyBaseInfo
  })

  const effectivePrincipalResolutionContext = useMemo(
    () =>
      buildStoreRelationEffectivePrincipalResolutionContext({
        principalResolutionContext,
        principalCandidates: Array.isArray(effectiveStoreRelationData?.principal_candidates)
          ? effectiveStoreRelationData.principal_candidates
          : [],
        principalUserOption: formState.principalUserOption
      }),
    [
      effectiveStoreRelationData?.principal_candidates,
      formState.principalUserOption,
      principalResolutionContext
    ]
  )

  const handleSave = async () => {
    if (saveInFlightRef.current) return

    if (!canSaveForm) {
      setShowValidationErrors(true)
      return
    }

    if (hasPendingPrincipalReassignment || hasPendingUserReassignments) {
      setSaveErrorMessage('')
      return
    }

    setSaveErrorMessage('')
    saveInFlightRef.current = true
    setIsSaving(true)

    try {
      const response = await saveStoreRelation({
        formState,
        isEditMode: isResolvedEditMode,
        relationId: flowState.relationId || id,
        canSplitOrderByBrand,
        principalResolutionContext: effectivePrincipalResolutionContext
      })

      if (response?.flow?.mode === STORE_RELATION_FORM_MODES.EDIT && response?.result) {
        applyExistingRelationFlow(response)
        return
      }

      const nextRelationId = response?.result?.client_principal?.id
      const successAlertMessage = isResolvedEditMode
        ? STORE_RELATIONS_SAVE_SUCCESS_MESSAGES.EDITED
        : STORE_RELATIONS_SAVE_SUCCESS_MESSAGES.CREATED

      if (nextRelationId) {
        navigate(`/store_relations/${nextRelationId}`, {
          state: {
            storeRelationData: response.result,
            returnTo: '/store_relations',
            successAlertMessage
          }
        })
      }
    } catch (error) {
      setSaveErrorMessage(
        error?.response?.errors?.join(' | ') ||
          error.message ||
          'No fue posible guardar la relación.'
      )
    } finally {
      saveInFlightRef.current = false
      setIsSaving(false)
    }
  }

  const handleAssignmentBrandChange = (rowId, nextBrandOption, nextBrandSearch) => {
    setFormState((prev) => ({
      ...prev,
      assignmentRows: prev.assignmentRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              brandOptions:
                typeof nextBrandSearch === 'string' ? row.brandOptions : nextBrandOption,
              brandSearch: typeof nextBrandSearch === 'string' ? nextBrandSearch : ''
            }
          : row
      )
    }))
  }

  const selectedBrandDbRefsByRow = useMemo(
    () => buildStoreRelationSelectedBrandDbRefsByRow(formState.assignmentRows),
    [formState.assignmentRows]
  )

  const initialAssignedBrandDbRefs = useMemo(
    () => buildStoreRelationInitialAssignedBrandDbRefs(initialFormState.assignmentRows),
    [initialFormState.assignmentRows]
  )

  const initialBrandOptionsByDbRef = useMemo(
    () =>
      buildStoreRelationInitialBrandOptionsByDbRef(
        initialFormState.assignmentRows,
        unavailableBrandDbRefs
      ),
    [initialFormState.assignmentRows, unavailableBrandDbRefs]
  )

  const handleAssignmentSalesmanSelect = (nextOption) => {
    if (!nextOption) return

    setFormState((prev) => {
      const alreadyExists = prev.assignmentRows.some((row) => row.id === nextOption.id)

      if (alreadyExists) {
        return {
          ...prev,
          assignmentUserSearch: ''
        }
      }

      return {
        ...prev,
        shouldAssignUsersByBrand: true,
        assignmentUserSearch: '',
        assignmentRows: [...prev.assignmentRows, buildStoreRelationNewAssignmentRow(nextOption)]
      }
    })
  }

  const currentReassignRow = useMemo(
    () =>
      formState.assignmentRows.find((row) => row.id === reassignModalState.currentRowId) || null,
    [formState.assignmentRows, reassignModalState.currentRowId]
  )

  const reassignUserOptions = useMemo(
    () =>
      buildStoreRelationReassignUserOptions({
        assignmentRows: formState.assignmentRows,
        currentRowId: reassignModalState.currentRowId,
        currentRowDbRef: currentReassignRow?.salesmanDbRef || '',
        searchTerm: reassignModalState.nextUserSearch,
        searchedOptions: reassignSalesmanOptions
      }),
    [
      currentReassignRow?.salesmanDbRef,
      formState.assignmentRows,
      reassignModalState.currentRowId,
      reassignModalState.nextUserSearch,
      reassignSalesmanOptions
    ]
  )

  const openReassignModal = (row) => {
    setReassignModalState({
      ...createStoreRelationReassignModalState(),
      open: true,
      currentRowId: row.id
    })
  }

  const closeReassignModal = () => {
    setReassignModalState(createStoreRelationReassignModalState())
  }

  const handleRemoveAssignmentRow = (rowId) => {
    setFormState((prev) => ({
      ...prev,
      assignmentRows: prev.assignmentRows.filter((row) => row.id !== rowId)
    }))

    if (reassignModalState.currentRowId === rowId) {
      closeReassignModal()
    }
  }

  const handleConfirmReassign = () => {
    if (!currentReassignRow || !reassignModalState.nextUser) return

    setFormState((prev) => {
      const currentRow = prev.assignmentRows.find((row) => row.id === currentReassignRow.id)

      if (!currentRow) return prev

      const remainingRows = prev.assignmentRows.filter((row) => row.id !== currentRow.id)
      const existingTargetRow = remainingRows.find(
        (row) =>
          row.id === reassignModalState.nextUser.id ||
          (row.salesmanDbRef &&
            reassignModalState.nextUser.db_ref &&
            row.salesmanDbRef === reassignModalState.nextUser.db_ref)
      )

      if (existingTargetRow) {
        return {
          ...prev,
          assignmentRows: remainingRows.map((row) => {
            if (row.id !== existingTargetRow.id) return row

            return {
              ...row,
              brandOptions: mergeStoreRelationBrandOptions(
                row.brandOptions || [],
                currentRow.brandOptions || []
              ),
              allBrandsInError: false,
              requiresReassignment: false,
              status: 'active'
            }
          })
        }
      }

      return {
        ...prev,
        assignmentRows: [
          ...remainingRows,
          buildStoreRelationNewAssignmentRow(reassignModalState.nextUser, {
            brandOptions: currentRow.brandOptions || []
          })
        ]
      }
    })

    closeReassignModal()
  }

  const infoAlertMessage =
    pageInfoMessage ||
    (flowState.mode === STORE_RELATION_FORM_MODES.EDIT && !isEditMode ? flowState.message : '')

  const handleCloseInfoAlert = () => {
    if (pageInfoMessage) {
      setPageInfoMessage('')
      return
    }

    setFlowState((prev) => ({
      ...prev,
      message: ''
    }))
  }

  if (isEditMode && loading) {
    return <LoadingScreen text={STORE_RELATION_FORM_TEXT.LOADING} />
  }

  return (
    <>
      <StoreRelationFormAlerts
        infoMessage={infoAlertMessage}
        onCloseInfo={handleCloseInfoAlert}
        saveErrorMessage={saveErrorMessage}
        onCloseSaveError={() => setSaveErrorMessage('')}
        brandValidationAlerts={brandValidationAlerts}
        onCloseBrandValidation={() => setBrandValidationAlerts([])}
        reassignmentWarningMessages={reassignmentWarningMessages}
        showBrandAssignmentsDisabledAlert={showBrandAssignmentsDisabledAlert}
      />

      <div className="main-show-header">
        <h2>{pageTitle}</h2>
      </div>

      <div className="index-table">
        <div className="show-area store-relations-form">
          <StoreRelationFormHeader
            pageDescription={pageDescription}
            isSaving={isSaving}
            saveButtonLabel={saveButtonLabel}
            onCancel={() => navigate(returnTo)}
            onSave={handleSave}
          />

          <hr />

          <StoreRelationBaseInfoSection
            shouldRenderReadOnlyBaseInfo={shouldRenderReadOnlyBaseInfo}
            readOnlyOptions={editBaseInfoOptions}
            formState={formState}
            supplierOptionsState={supplierOptionsState}
            buyerOptionsState={buyerOptionsState}
            referenceOptionsState={referenceOptionsState}
            clientOptionsState={clientOptionsState}
            isClientStepCompleted={isClientStepCompleted}
            showValidationErrors={showValidationErrors}
            onSupplierChange={handleSupplierChange}
            onBuyerChange={handleBuyerChange}
            onReferenceChange={handleReferenceChange}
            onClientChange={handleClientChange}
            onSearchChange={handleSearchChange}
          />

          <hr />

          <StoreRelationPrincipalSection
            isClientStepCompleted={isClientStepCompleted}
            principalSalesmanOptions={filteredPrincipalSalesmanOptions}
            principalSalesmenLoading={principalSalesmenLoading}
            principalUserOption={formState.principalUserOption}
            principalUserSearch={formState.principalUserSearch}
            showValidationErrors={showValidationErrors}
            hasPendingPrincipalReassignment={hasPendingPrincipalReassignment}
            canSplitOrderByBrand={canSplitOrderByBrand}
            shouldAssignUsersByBrand={formState.shouldAssignUsersByBrand}
            onPrincipalUserChange={handlePrincipalUserChange}
            onPrincipalUserSearchChange={(nextInputValue) => {
              handleSearchChange('principalUserSearch', nextInputValue)
            }}
            onToggleAssignUsersByBrand={handleToggleAssignUsersByBrand}
          />

          {canSplitOrderByBrand && formState.shouldAssignUsersByBrand && (
            <StoreRelationBrandAssignmentsSection
              isEditMode={isEditMode}
              clientDbRef={formState.clientOption?.db_ref || ''}
              assignmentRows={formState.assignmentRows}
              assignmentUserSearch={formState.assignmentUserSearch}
              assignmentSalesmanOptions={assignmentSalesmanOptions}
              assignmentSalesmenLoading={assignmentSalesmenLoading}
              selectedBrandDbRefsByRow={selectedBrandDbRefsByRow}
              initialAssignedBrandDbRefs={initialAssignedBrandDbRefs}
              initialBrandOptionsByDbRef={initialBrandOptionsByDbRef}
              unavailableBrandDbRefs={unavailableBrandDbRefs}
              onAssignmentSalesmanSelect={handleAssignmentSalesmanSelect}
              onAssignmentUserSearchChange={(nextInputValue) => {
                handleSearchChange('assignmentUserSearch', nextInputValue)
              }}
              onAssignmentBrandChange={handleAssignmentBrandChange}
              onOpenReassignModal={openReassignModal}
              onRemoveAssignmentRow={handleRemoveAssignmentRow}
            />
          )}
        </div>
      </div>

      <ReassignUserModal
        show={reassignModalState.open}
        currentUser={buildStoreRelationCurrentReassignUser(currentReassignRow)}
        nextUser={reassignModalState.nextUser}
        nextUserOptions={reassignUserOptions}
        nextUserSearch={reassignModalState.nextUserSearch}
        loading={reassignSalesmenLoading}
        onClose={closeReassignModal}
        onConfirm={handleConfirmReassign}
        onNextUserChange={(nextUser) => {
          setReassignModalState((prev) => ({
            ...prev,
            nextUser,
            nextUserSearch: nextUser?.label || prev.nextUserSearch
          }))
        }}
        onNextUserSearchChange={(nextUserSearch) => {
          setReassignModalState((prev) => ({
            ...prev,
            nextUserSearch,
            nextUser: null
          }))
        }}
      />
    </>
  )
}

export default StoreRelationFormView
